import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Image,
  NavigationHeader,
  Link,
  Separator,
  Text,
  Button,
  OverlayProvider,
} from "@pingux/astro";
import { menuItems, HEADER_LOGO, DISPLAY_NAME } from "./HeaderConfig";
import HelpButton from "./HelpButton";
import { headerSX } from "./HeaderSX";
import { useAuthContext } from "state/context/AuthContext";
import { requestsService } from "@services/requests";

const Header = () => {
  const navigate = useNavigate();
  const {
    state: { isAuthenticated, user },
    dispatch,
  } = useAuthContext();
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const userName = isAuthenticated ? user?.name ?? 'Unknown' : 'Unknown';
  const userRole = isAuthenticated ? user?.role ?? 'user' : 'user';
  const isAdminUser = userRole === 'admin' || userRole === 'superadmin';

  useEffect(() => {
    if (!isAdminUser || !isAuthenticated) return;
    requestsService.getPendingCount().then(setPendingCount).catch((_e) => undefined);
  }, [isAdminUser, isAuthenticated]);

  const handleDownloadExtension = async () => {
    const response = await fetch('/api/extension/download', {
      credentials: 'include',
    });
    if (response.status === 401) {
      window.location.assign('/api/auth/logout');
      return;
    }
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tap-partner-vault-extension.zip';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    dispatch({ type: "UNAUTHENTICATED" });
    window.location.assign("/api/auth/logout");
  };

  return (
    <NavigationHeader
      sx={headerSX.headerContainer}
      aria-labelledby="tap-partner-vault-header"
    >
      <Box
        isRow
        alignItems="center"
        justifyContent="center"
        py="sm"
        flex="0 0 auto"
      >
        <Link onPress={() => navigate("/")}>
          <Image
            src={HEADER_LOGO}
            alt="Ping Identity Logo"
            mr="md"
            sx={{
              height: "24px",
              cursor: "pointer",
            }}
          />
        </Link>

        <Separator
          orientation="vertical"
          style={{
            height: "28px",
            margin: "0",
          }}
        />
        <Link
          variant="navigationHeader.logoBand"
          onPress={() => navigate("/")}
        >
          <Text
            as="h2"
            variant="navigationHeader.headerPlaceholder"
            id="tap-partner-vault-header"
            sx={{
              cursor: "pointer",
              color: "rgba(0,0,0,0.9)",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {DISPLAY_NAME}
          </Text>
        </Link>
      </Box>
      <OverlayProvider>
        <Box as="ul" isRow alignItems="center" p="0" ml="auto" flex="0 0 auto" sx={{ gap: '0.75rem' }}>
          {isAdminUser && (
            <button
              onClick={() => navigate('/requests')}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.3rem 0.75rem',
                fontSize: '0.8rem',
                fontWeight: '500',
                color: '#374151',
                backgroundColor: pendingCount > 0 ? '#fef3c7' : '#f3f4f6',
                border: `1px solid ${pendingCount > 0 ? '#f59e0b' : '#d1d5db'}`,
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              Requests
              {pendingCount > 0 && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '1.1rem',
                  height: '1.1rem',
                  padding: '0 0.25rem',
                  fontSize: '0.65rem',
                  fontWeight: '700',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '9999px',
                }}>
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          {isAdminUser && (
            <button
              onClick={() => navigate('/validation')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.3rem 0.75rem',
                fontSize: '0.8rem',
                fontWeight: '500',
                color: '#374151',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
              }}
            >
              Validation
            </button>
          )}
          <Box sx={{ textAlign: 'right', lineHeight: '1.2' }}>
            <Text sx={{ fontSize: '0.8rem', fontWeight: '600', color: 'rgba(0,0,0,0.85)', display: 'block' }}>
              {userName}
            </Text>
            <Text sx={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)', textTransform: 'capitalize', display: 'block' }}>
              {userRole}
            </Text>
          </Box>
          <HelpButton items={menuItems} onLogout={handleLogout} onInstallExtension={() => setShowExtensionModal(true)} />
        </Box>
      </OverlayProvider>

      {showExtensionModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '0.5rem',
            padding: '2rem', maxWidth: '480px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <Text as="h2" sx={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
              Install TAP Partner Vault Extension
            </Text>
            <ol style={{ paddingLeft: '1.25rem', lineHeight: '2', color: '#374151', fontSize: '0.875rem' }}>
              <li>Download the extension using the button below</li>
              <li>Unzip the downloaded file</li>
              <li>Open Chrome and navigate to <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '0.25rem' }}>chrome://extensions</code></li>
              <li>Enable <strong>Developer mode</strong> (top-right toggle)</li>
              <li>Click <strong>Load unpacked</strong> and select the unzipped folder</li>
              <li>The extension will appear in your toolbar</li>
            </ol>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', gap: '1rem' }}>
              <button
                onClick={handleDownloadExtension}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  backgroundColor: '#2563eb', color: 'white', fontWeight: '500',
                  fontSize: '0.875rem', padding: '0.5rem 1rem', borderRadius: '0.375rem',
                  border: 'none', cursor: 'pointer',
                }}
              >
                Download Extension
              </button>
              <Button variant="default" onPress={() => setShowExtensionModal(false)}>
                Close
              </Button>
            </Box>
          </div>
        </div>
      )}
    </NavigationHeader>
  );
};

export default Header;
